// ============================================================
// CLUBHUB – Die Zuwendungsbestätigung als Blatt (amtliches Muster)
// ============================================================
// Drei Fassungen nach den verbindlichen Mustern des BMF fuer
// Koerperschaften nach § 5 Abs. 1 Nr. 9 KStG:
//
//   GELD     Bestätigung über Geldzuwendungen (auch Aufwandsverzicht,
//            dann „Verzicht auf Erstattung von Aufwendungen: ja“)
//   SACH     Bestätigung über Sachzuwendungen — mit Bezeichnung,
//            Alter, Zustand, Herkunft und Wertermittlung
//   SAMMEL   Sammelbestätigung über Geldzuwendungen mit Anlage der
//            Einzelzuwendungen
//
// Der Text steht in `lib/spenden/belegtext.ts` — dieselbe Quelle wie
// das PDF (`lib/spenden/beleg-pdf.tsx`).
//
// Ohne Hooks: dasselbe Blatt steht auf der Seite einer Bestätigung
// (Server) und in der Vorschau des Assistenten (Browser). Fehlt eine
// Pflichtangabe, steht an ihrer Stelle eine rote Luecke.
// ============================================================

import { Check } from "lucide-react";
import { cn, formatDate, formatDateTime } from "@/lib/utils";
import { betragInWorten, euro, HERKUNFT, WERTERMITTLUNG, zeitraumText } from "@/lib/spenden/format";
import {
  BELEG_TITEL as TITEL,
  BELEG_UNTERTITEL as UNTERTITEL,
  befreiungsSatz,
  HINWEIS_SAETZE,
  SACH_HERKUNFT,
  SAMMEL_SAETZE,
  verwendungsSatz,
  type BelegblattDaten,
  type Stueck,
} from "@/lib/spenden/belegtext";
import type { Sachangaben } from "@/lib/spenden/views";

export { belegDaten, type BelegblattDaten } from "@/lib/spenden/belegtext";

/** Eine fehlende Pflichtangabe — rot, damit sie niemand uebersieht. */
function Luecke({ was }: { was: string }) {
  return <span className="bg-danger-subtle text-danger rounded px-1 font-medium">[{was} fehlt]</span>;
}

function Wert({ wert, was }: { wert: string | null | undefined; was: string }) {
  return wert ? <span className="text-text-primary font-medium">{wert}</span> : <Luecke was={was} />;
}

/** Ein Satz aus Text und eingesetzten Werten. */
function Satz({ teile }: { teile: Stueck[] }) {
  return (
    <>
      {teile.map((t, i) => (typeof t === "string" ? <span key={i}>{t}</span> : <Wert key={i} wert={t.wert} was={t.was} />))}
    </>
  );
}

function Kaestchen({ an }: { an: boolean }) {
  return (
    <span aria-hidden className="border-text-secondary mt-[3px] inline-grid h-3.5 w-3.5 shrink-0 place-items-center rounded-[3px] border">
      {an && <Check className="text-text-primary h-3 w-3" strokeWidth={3} />}
    </span>
  );
}

function Auswahl({ an, children }: { an: boolean; children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2">
      <Kaestchen an={an} />
      <span className="sr-only">{an ? "angekreuzt: " : "nicht angekreuzt: "}</span>
      <span className={cn(!an && "text-text-tertiary")}>{children}</span>
    </p>
  );
}

/** Ein Feld des Vordrucks: Beschriftung klein darueber, Inhalt in einem Kasten. */
function Feld({ titel, children, className }: { titel: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="text-caption text-text-tertiary mb-1">{titel}</p>
      <div className="border-border min-h-[38px] rounded-md border px-3 py-2">{children}</div>
    </div>
  );
}

export function Belegblatt({ d, className }: { d: BelegblattDaten; className?: string }) {
  const a = d.aussteller;
  const sammel = d.form === "SAMMEL";
  const sach = d.form === "SACH";

  return (
    <article
      data-belegblatt=""
      aria-label={TITEL[d.form]}
      className={cn(
        "bg-bg-elevated shadow-sheet-body text-body-sm text-text-secondary relative mx-auto w-full max-w-[820px] rounded-lg ring-1 ring-black/[0.05] dark:ring-white/[0.06]",
        "px-12 py-11 max-md:px-5 max-md:py-6",
        className,
      )}
    >
      {d.widerrufen && (
        <div className="border-danger/30 bg-danger-subtle text-danger mb-6 rounded-lg border px-4 py-2.5">
          <p className="font-medium">Widerrufen am {formatDateTime(d.widerrufen.am)}</p>
          {d.widerrufen.grund && <p className="text-caption">{d.widerrufen.grund}</p>}
          {d.widerrufen.ersetztDurch && <p className="text-caption">Ersetzt durch {d.widerrufen.ersetztDurch}.</p>}
        </div>
      )}

      {/* Kopf: Aussteller links, Nummer rechts */}
      <header className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="min-w-0">
          <p className="text-caption text-text-tertiary">Aussteller (Bezeichnung und Anschrift der steuerbegünstigten Einrichtung)</p>
          <p className="text-body-md text-text-primary mt-1 font-medium">{a.name || <Luecke was="Name des Vereins" />}</p>
          {a.anschrift.length > 0 ? a.anschrift.map((z) => <p key={z}>{z}</p>) : <Luecke was="Anschrift" />}
        </div>
        <dl className="text-caption grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-right">
          <dt className="text-text-tertiary">Nummer</dt>
          <dd className="text-text-primary font-medium tabular-nums">{d.nummer ?? (d.vorschau ? "wird vergeben" : "–")}</dd>
          <dt className="text-text-tertiary">Ausgestellt</dt>
          <dd className="text-text-primary tabular-nums">{formatDate(d.ausgestelltAm)}</dd>
        </dl>
      </header>

      <h1 className="text-heading-md text-text-primary mt-8">{TITEL[d.form]}</h1>
      <p className="text-caption text-text-tertiary mt-1 max-w-[62ch]">{UNTERTITEL}</p>

      <div className="mt-6 space-y-4">
        <Feld titel="Name und Anschrift des Zuwendenden:">
          <p className="text-text-primary font-medium">{d.empfaenger.name}</p>
          {d.empfaenger.zeilen.length > 0 ? d.empfaenger.zeilen.map((z) => <p key={z}>{z}</p>) : <Luecke was="Anschrift" />}
        </Feld>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,11rem)]">
          <Feld titel={`${sammel ? "Gesamtbetrag" : sach ? "Wert" : "Betrag"} der Zuwendung – in Ziffern –`}>
            <span className="text-text-primary font-medium tabular-nums">{euro(d.betrag)}</span>
          </Feld>
          <Feld titel="– in Buchstaben –">
            <span className="text-text-primary">– {betragInWorten(d.betrag)} –</span>
          </Feld>
          <Feld titel={sammel ? "Zeitraum der Sammelbestätigung:" : "Tag der Zuwendung:"}>
            <span className="text-text-primary tabular-nums">{sammel ? zeitraumText(d.von, d.bis) : formatDate(d.von)}</span>
          </Feld>
        </div>

        {sach && (
          <>
            <Feld titel="Genaue Bezeichnung der Sachzuwendung mit Alter, Zustand, Kaufpreis usw.">
              {d.sach ? (
                <>
                  <p className="text-text-primary font-medium">{d.sach.bezeichnung}</p>
                  <p>{d.sach.alterZustand}</p>
                  <p className="text-caption">
                    Wert ermittelt über: {WERTERMITTLUNG[d.sach.wertermittlung]}
                    {d.sach.wertGrundlage ? ` (${d.sach.wertGrundlage})` : ""}
                  </p>
                </>
              ) : (
                <Luecke was="Bezeichnung" />
              )}
            </Feld>
            <div className="space-y-1.5">
              <Auswahl an={d.sach?.herkunft === "BETRIEB"}>{SACH_HERKUNFT.BETRIEB}</Auswahl>
              <Auswahl an={d.sach?.herkunft === "PRIVAT"}>{SACH_HERKUNFT.PRIVAT}</Auswahl>
              <Auswahl an={d.sach?.herkunft === "UNBEKANNT"}>{SACH_HERKUNFT.UNBEKANNT}</Auswahl>
              <Auswahl an={Boolean(d.sach?.unterlagenLiegenVor)}>{SACH_HERKUNFT.UNTERLAGEN}</Auswahl>
            </div>
          </>
        )}

        {d.form === "GELD" && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span>Es handelt sich um den Verzicht auf Erstattung von Aufwendungen:</span>
            <span className="inline-flex items-start gap-1.5">
              <Kaestchen an={d.verzicht} /> Ja
            </span>
            <span className="inline-flex items-start gap-1.5">
              <Kaestchen an={!d.verzicht} /> Nein
            </span>
            <span className="sr-only">{d.verzicht ? "Ja" : "Nein"}</span>
          </div>
        )}

        <Auswahl an>
          <Satz teile={befreiungsSatz(a)} />
        </Auswahl>

        <p>
          <Satz teile={verwendungsSatz(a)} />
        </p>

        {sammel && SAMMEL_SAETZE.map((t) => <p key={t}>{t}</p>)}

        {/* Ort, Datum, Unterschrift */}
        <div className="grid grid-cols-1 gap-6 pt-6 sm:grid-cols-2">
          <div>
            <p className="text-text-primary border-text-secondary border-b pb-1">
              {a.ort ?? <Luecke was="Ort" />}, {formatDate(d.ausgestelltAm)}
            </p>
            <p className="text-caption text-text-tertiary mt-1">Ort, Datum</p>
          </div>
          <div>
            <div aria-hidden className="border-text-secondary h-[25px] border-b" />
            <p className="text-caption text-text-tertiary mt-1">Unterschrift des Zuwendungsempfängers{a.signedBy ? ` · ${a.signedBy}` : ""}</p>
          </div>
        </div>

        <div className="border-border-subtle text-caption text-text-tertiary mt-6 space-y-1.5 border-t pt-4">
          <p className="text-text-secondary font-medium">Hinweis:</p>
          {HINWEIS_SAETZE.map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
      </div>

      {sammel && (
        <section className="border-border mt-10 border-t pt-8 print:break-before-page" aria-label="Anlage zur Sammelbestätigung">
          <h2 className="text-heading-sm text-text-primary">Anlage zur Sammelbestätigung</h2>
          <p className="text-caption text-text-tertiary mt-1">
            {d.nummer ? `zu ${d.nummer} · ` : ""}
            {d.empfaenger.name} · {zeitraumText(d.von, d.bis)}
          </p>
          <table className="mt-4 w-full border-collapse">
            <thead>
              <tr className="border-border text-caption text-text-tertiary border-b text-left align-bottom">
                <th className="py-2 pr-3 font-medium">Datum der Zuwendung</th>
                <th className="py-2 pr-3 font-medium">Art der Zuwendung</th>
                <th className="py-2 pr-3 font-medium">Verzicht auf die Erstattung von Aufwendungen (ja/nein)</th>
                <th className="py-2 text-right font-medium">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {d.posten.map((p) => (
                <tr key={p.donationId} className="border-border-subtle border-b">
                  <td className="py-1.5 pr-3 tabular-nums">{formatDate(p.date)}</td>
                  <td className="py-1.5 pr-3">Geldzuwendung</td>
                  <td className="py-1.5 pr-3">{p.kind === "WAIVER" ? "ja" : "nein"}</td>
                  <td className="text-text-primary py-1.5 text-right tabular-nums">{euro(p.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="text-text-primary pt-2.5 font-medium">
                  Gesamtsumme
                </td>
                <td className="text-text-primary pt-2.5 text-right font-medium tabular-nums">{euro(d.posten.reduce((s, p) => s + p.amount, 0))}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      )}

      {d.vorschau && (
        <p className="text-caption text-text-tertiary mt-8 text-center">Vorschau — die Nummer vergibt clubhub beim Ausstellen.</p>
      )}
    </article>
  );
}

/** Wie das Blatt zu drucken ist: nur das Blatt, A4, ohne Huelle. */
export const BELEG_DRUCK = `@media print {
  @page { size: A4; margin: 14mm 16mm; }
  body * { visibility: hidden !important; }
  [data-belegdruck], [data-belegdruck] * { visibility: visible !important; }
  [data-belegdruck] { position: absolute !important; left: 0; top: 0; width: 100%; }
  [data-belegblatt] { box-shadow: none !important; max-width: none !important; padding: 0 !important; --tw-ring-shadow: 0 0 #0000 !important; }
}`;

/** Herkunft kurz — fuer Listen. */
export const herkunftText = (s: Sachangaben | null) => (s ? HERKUNFT[s.herkunft].label : null);
